import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getSchool } from '../../services/schoolService';
import { getAllStates } from '../../services/locationService';
import Loader from '../common/Loader';
import { formatDate } from '../org/orgFormUtils';

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">{label}</p>
      <p className="text-sm text-gray-900 dark:text-white break-words">{value || '—'}</p>
    </div>
  );
}

export default function ViewSchool() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [stateLabel, setStateLabel] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAllStates()
      .then((res) => {
        const states = res.data?.data || [];
        const match = states.find((s) => s.state_code === school?.state_code);
        if (match) setStateLabel(`${match.state_name} (${match.state_code})`);
        else if (school?.state_code) setStateLabel(school.state_code);
      })
      .catch(() => {
        if (school?.state_code) setStateLabel(school.state_code);
      });
  }, [school?.state_code]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await getSchool(id);
        setSchool(data.data);
      } catch {
        navigate('/schools');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, navigate]);

  if (loading) return <Loader text="Loading school..." />;
  if (!school) return null;

  const grades = school.grade_low || school.grade_high
    ? `${school.grade_low || '?'} – ${school.grade_high || '?'}`
    : '—';

  return (
    <div className="mx-auto w-full min-w-0 max-w-[min(100%,56rem)] space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/schools" className="p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">School Details</h1>
        </div>
        <Link
          to={`/schools/${id}/edit`}
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-indigo-200 dark:border-indigo-500/40 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-600/10 text-sm font-medium transition-colors"
        >
          Edit
        </Link>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl p-5 sm:p-6 space-y-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{school.school_name}</h2>
          <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">{school.district_name || '—'}</p>
          <span className={`inline-block mt-2 px-2.5 py-1 rounded-full text-xs font-medium border ${school.is_active ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800' : 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800'}`}>
            {school.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Detail label="NCES School ID" value={school.nces_school_id} />
          <Detail label="School Type" value={school.school_type} />
          <Detail label="Grades" value={grades} />
          <Detail label="District / LEA" value={school.district_name} />
          <Detail label="LEA ID" value={school.lea_id} />
          <Detail label="State" value={stateLabel || school.state_code} />
          <Detail label="City" value={school.city} />
          <Detail label="Zipcode" value={school.zipcode} />
          <Detail label="Phone" value={school.phone} />
          <Detail label="Address" value={school.address_line1} />
          <Detail label="Created" value={formatDate(school.created_at)} />
          <Detail label="Updated" value={formatDate(school.updated_at)} />
        </div>
      </div>
    </div>
  );
}
